// Merges everything the planner is still unsure about into one ranked queue.
//
// Rating entries come from the decision-value calculation the card deck used,
// clash entries from overlaps found in a built plan, group entries from the
// conflict detector, and preference entries from fields left blank.
//
// Every entry carries an impact between zero and one. Scores inside one family
// are normalized against the largest in that family, then multiplied by a weight
// for the family, so a clash in a built plan outranks a rating for an unheard
// artist.
//
// Reads no store, no database and no DOM.

import type { Artist, Conflict, Id, MustSee, Profile } from './types.ts';
import type { Clash } from './picks.ts';
import { optionsForClash } from './picks.ts';
import { triageOrder } from './decisionValue.ts';
import type { DecisionValueInput } from './decisionValue.ts';
import { COMPANY_OPTIONS, GOAL_OPTIONS } from './preference-options.ts';
import { overlapMs } from './time.ts';

export type DecisionType = 'rate' | 'clash' | 'priority' | 'preference' | 'group';
export type DecisionStatus = 'open' | 'answered' | 'dismissed';

export interface DecisionOption {
  id: string;
  label: string;
  sub?: string;
  // Carries what the caller writes when this option is chosen.
  apply?: unknown;
}

export interface Decision {
  id: string;
  type: DecisionType;
  // Zero to one. Higher means answering it changes more.
  impact: number;
  title: string;
  context: string;
  options: DecisionOption[];
  relatedArtistIds: Id[];
  relatedSetIds: Id[];
  dayId?: Id;
  status?: DecisionStatus;
}

// What a family is worth relative to the others, before its own scores apply.
const WEIGHT: Record<DecisionType, number> = {
  clash: 1,
  preference: 0.95,
  group: 0.9,
  rate: 0.8,
  priority: 0.6,
};

const RATE_OPTIONS: DecisionOption[] = [
  { id: 'love', label: 'Love', sub: 'Build the day around it', apply: { score: 10 } },
  { id: 'like', label: 'Like', sub: 'Worth catching', apply: { score: 7 } },
  { id: 'none', label: 'No preference', sub: 'Let the planner decide', apply: { skip: true } },
  { id: 'no', label: 'Not for me', sub: 'Leave it out', apply: { score: 2 } },
];

/** Scales a list of raw scores into zero to one against the largest of them. */
export function normalizeImpact(values: number[]): (value: number) => number {
  const top = Math.max(0, ...values);
  if (top <= 0) return () => 0;
  return (value) => Math.min(1, Math.max(0, value / top));
}

export interface DecisionInput {
  artists: Artist[];
  // The shape the decision-value calculation already takes.
  triage: DecisionValueInput;
  // Artists carrying a rating set by hand.
  ratedArtistIds: Set<Id>;
  // Overlaps and tight walks in the plan, by day.
  clashesByDay: Map<Id, Clash[]>;
  // Contested windows between members, by day.
  conflictsByDay: Map<Id, Conflict[]>;
  // As stored: a blank goal or company answer is an absent field.
  profile: ProfileAnswers | undefined;
  // Size of the crew, the account holder included.
  memberCount: number;
  nameOfSet: (setId: Id) => string;
  formatTime: (ts: number) => string;
  // Cap on rating entries.
  rateLimit?: number;
  // The person's top ten and their hand-set scores, which priority questions
  // are drawn from. Absent means no priority questions.
  mustSee?: MustSee[];
  manualScores?: Map<Id, number>;
}

export type ProfileAnswers = Pick<Profile, 'subgenreWeights'> &
  Partial<Pick<Profile, 'goalPriority' | 'alignmentIntent'>>;

function preferenceDecisions(profile: ProfileAnswers | undefined, memberCount: number): Decision[] {
  const out: Decision[] = [];
  const weights = profile?.subgenreWeights ?? {};

  if (Object.keys(weights).length === 0) {
    out.push({
      id: 'preference:taste',
      type: 'preference',
      impact: WEIGHT.preference,
      title: 'What sounds like your weekend?',
      context: 'Marking even two or three genres changes what the first draft reaches for.',
      options: [{ id: 'open', label: 'Set my preferences', apply: { route: '/preferences' } }],
      relatedArtistIds: [],
      relatedSetIds: [],
    });
  }

  if (profile && !profile.goalPriority) {
    out.push({
      id: 'preference:goal',
      type: 'preference',
      impact: WEIGHT.preference * 0.8,
      title: 'What should the planner favor?',
      context: 'This only affects the first draft. Every choice stays changeable.',
      options: GOAL_OPTIONS.map((goal) => ({
        id: goal.value,
        label: goal.label,
        sub: goal.sub,
        apply: { preferences: { goalPriorities: [goal.value] } },
      })),
      relatedArtistIds: [],
      relatedSetIds: [],
    });
  }

  // Asking who a plan is for is a question only when somebody can be named.
  if (memberCount > 1 && profile && !profile.alignmentIntent) {
    out.push({
      id: 'preference:alignment',
      type: 'preference',
      impact: WEIGHT.preference * 0.7,
      title: 'Who should this plan account for?',
      context: `There ${memberCount === 2 ? 'is 1 other person' : `are ${memberCount - 1} other people`} in your crew.`,
      options: COMPANY_OPTIONS.map((company) => ({
        id: company.value,
        label: company.label,
        sub: company.sub,
        apply: { preferences: { alignmentIntent: company.value } },
      })),
      relatedArtistIds: [],
      relatedSetIds: [],
    });
  }

  return out;
}

function clashDecisions(
  clashesByDay: Map<Id, Clash[]>,
  nameOfSet: (setId: Id) => string,
  formatTime: (ts: number) => string,
): Decision[] {
  const all: { dayId: Id; clash: Clash }[] = [];
  for (const [dayId, list] of clashesByDay) {
    for (const clash of list) all.push({ dayId, clash });
  }
  if (all.length === 0) return [];

  // Minutes at stake: shared minutes for an overlap, and the minutes a walk
  // falls short by for a tight gap.
  const stake = ({ clash }: { clash: Clash }) =>
    clash.kind === 'overlap' ? clash.overlapMinutes : Math.max(0, clash.shortByMinutes);
  const scale = normalizeImpact(all.map(stake));

  return all.map(({ dayId, clash }) => {
    const first = nameOfSet(clash.first.setId);
    const second = nameOfSet(clash.second.setId);
    return {
      id: `clash:${clash.id}`,
      type: 'clash' as const,
      impact: WEIGHT.clash * scale(stake({ clash })),
      title:
        clash.kind === 'overlap'
          ? `${first} and ${second} overlap`
          : `Tight walk between ${first} and ${second}`,
      context:
        clash.kind === 'overlap'
          ? `They share ${clash.overlapMinutes} minutes. Both are in your plan.`
          : `${clash.gapMinutes} minutes between them and the walk is about ${clash.walkMinutes}.`,
      options: optionsForClash(clash, nameOfSet, formatTime).map((option) => ({
        id: option.id,
        label: option.label,
        sub: option.minutesKept.map((k) => `${nameOfSet(k.setId)} ${k.minutes} min`).join(' and '),
        apply: { spans: option.spans, drops: option.drops },
      })),
      relatedArtistIds: [],
      relatedSetIds: [clash.first.setId, clash.second.setId],
      dayId,
    };
  });
}

function groupDecisions(conflictsByDay: Map<Id, Conflict[]>): Decision[] {
  const all: { dayId: Id; conflict: Conflict }[] = [];
  for (const [dayId, list] of conflictsByDay) {
    for (const conflict of list) all.push({ dayId, conflict });
  }
  if (all.length === 0) return [];

  const scale = normalizeImpact(all.map(({ conflict }) => conflict.severity));

  return all.map(({ dayId, conflict }) => ({
    id: `group:${conflict.id}`,
    type: 'group' as const,
    impact: WEIGHT.group * scale(conflict.severity),
    title: 'Your crew wants different things here',
    context:
      conflict.recommendationReason ??
      `${conflict.memberPositions.length} people are in separate places in this window.`,
    options: [{ id: 'open', label: 'Look at the options', apply: { conflictId: conflict.id } }],
    relatedArtistIds: [],
    relatedSetIds: conflict.options.flatMap((o) => o.segments.map((s) => s.setId)),
    dayId,
  }));
}

function rateDecisions(input: DecisionInput): Decision[] {
  const limit = input.rateLimit ?? 8;
  const ranked = triageOrder(input.artists, input.triage, input.ratedArtistIds).slice(0, limit);
  if (ranked.length === 0) return [];

  const scale = normalizeImpact(ranked.map((r) => r.value));

  return ranked.map(({ artist, value }) => ({
    id: `rate:${artist.id}`,
    type: 'rate' as const,
    impact: WEIGHT.rate * scale(value),
    title: `How interested are you in ${artist.name}?`,
    context:
      artist.genreTags.length > 0
        ? artist.genreTags.join(', ')
        : 'No genre on the bill for this artist.',
    options: RATE_OPTIONS.map((option) => ({
      ...option,
      apply: { artistId: artist.id, ...(option.apply as Record<string, unknown>) },
    })),
    relatedArtistIds: [artist.id],
    relatedSetIds: [],
  }));
}

// Two artists somebody cares about playing at the same time on different
// stages, with scores close enough that the planner cannot tell which matters
// more. Choosing one moves it above the other in the top ten.
function priorityDecisions(input: DecisionInput): Decision[] {
  const mustSee = input.mustSee ?? [];
  const scores = input.manualScores ?? new Map<Id, number>();
  const ranked = new Set(mustSee.map((m) => m.artistId));
  const scoreOf = (artistId: Id) => scores.get(artistId) ?? (ranked.has(artistId) ? 10 : undefined);
  const nameOf = (artistId: Id) => input.artists.find((a) => a.id === artistId)?.name ?? 'that artist';

  const found: { a: Id; b: Id; minutes: number; setA: Id; setB: Id; dayId: Id }[] = [];
  const seen = new Set<string>();
  for (const ctx of input.triage.days) {
    const cared = ctx.sets.filter((s) => ranked.has(s.artistId) || (scores.get(s.artistId) ?? 0) >= 8);
    for (let i = 0; i < cared.length; i++) {
      for (let j = i + 1; j < cared.length; j++) {
        const [x, y] = [cared[i], cared[j]];
        if (x.artistId === y.artistId || x.stageId === y.stageId) continue;
        const shared = overlapMs(x.startTs, x.endTs, y.startTs, y.endTs) / 60000;
        const sx = scoreOf(x.artistId);
        const sy = scoreOf(y.artistId);
        if (shared <= 0 || sx === undefined || sy === undefined || Math.abs(sx - sy) > 1) continue;
        const [first, second] = x.artistId < y.artistId ? [x, y] : [y, x];
        const key = `${first.artistId}:${second.artistId}`;
        if (seen.has(key)) continue;
        seen.add(key);
        found.push({ a: first.artistId, b: second.artistId, minutes: shared, setA: first.id, setB: second.id, dayId: ctx.day.id });
      }
    }
  }
  if (found.length === 0) return [];

  const scale = normalizeImpact(found.map((f) => f.minutes));
  return found.map((f) => ({
    id: `priority:${f.a}:${f.b}`,
    type: 'priority' as const,
    impact: WEIGHT.priority * scale(f.minutes),
    title: 'Which matters more to you?',
    context: `${nameOf(f.a)} and ${nameOf(f.b)} share ${Math.round(f.minutes)} minutes on different stages.`,
    options: [
      { id: f.a, label: nameOf(f.a), apply: { prefer: f.a, over: f.b } },
      { id: f.b, label: nameOf(f.b), apply: { prefer: f.b, over: f.a } },
    ],
    relatedArtistIds: [f.a, f.b],
    relatedSetIds: [f.setA, f.setB],
    dayId: f.dayId,
  }));
}

/** The top ten with `prefer` placed directly above `over`, adding either if absent. */
export function preferInOrder(order: Id[], prefer: Id, over: Id): Id[] {
  const rest = order.filter((id) => id !== prefer);
  const at = rest.indexOf(over);
  if (at < 0) return [...rest, prefer, over];
  return [...rest.slice(0, at), prefer, ...rest.slice(at)];
}

// Splits every generated decision into the open queue and the history of what
// was dismissed or answered. An id on neither list is open.
export function partitionDecisions(
  all: Decision[],
  dismissed: string[],
  answered: string[],
): { open: Decision[]; history: Decision[] } {
  const open: Decision[] = [];
  const history: Decision[] = [];
  for (const decision of all) {
    if (answered.includes(decision.id)) history.push({ ...decision, status: 'answered' });
    else if (dismissed.includes(decision.id)) history.push({ ...decision, status: 'dismissed' });
    else open.push({ ...decision, status: 'open' });
  }
  return { open, history };
}

// Builds the ranked queue, highest impact first, with a stable order inside one
// impact so the list does not reshuffle between renders.
export function buildDecisionQueue(input: DecisionInput): Decision[] {
  const decisions = [
    ...preferenceDecisions(input.profile, input.memberCount),
    ...clashDecisions(input.clashesByDay, input.nameOfSet, input.formatTime),
    ...groupDecisions(input.conflictsByDay),
    ...rateDecisions(input),
    ...priorityDecisions(input),
  ];

  return decisions
    .filter((decision) => decision.impact > 0 && decision.options.length > 0)
    .sort((a, b) => b.impact - a.impact || a.id.localeCompare(b.id));
}

/** A sentence saying how much is worth answering, or that nothing is. */
export function describeQueue(queue: Decision[]): string {
  if (queue.length === 0) return 'Nothing left that would change this plan.';
  const strong = queue.filter((d) => d.impact >= 0.5).length;
  const count = queue.length === 1 ? '1 quick choice' : `${queue.length} quick choices`;
  return strong > 0
    ? `${count} could improve this plan.`
    : `${count} left, none of them likely to change much.`;
}
